import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bell, ChevronDown, ChevronRight, Clock } from "lucide-react";

export default function TeamProgressTab() {
  const [data, setData] = useState({ trainees: [], total_lessons: 0, trainee_count: 0 });
  const [expanded, setExpanded] = useState({});
  const [nudging, setNudging] = useState({});

  const load = () => api.get("/admin/team-progress").then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const nudge = async (t) => {
    setNudging(n => ({ ...n, [t.id]: true }));
    try {
      const { data: res } = await api.post("/admin/nudge", { user_id: t.id });
      if (res.sent) toast.success(`Nudge emailed to ${t.name || t.email}`);
      else toast.warning(`Email not sent (${res.reason || "no key"}). Nudge logged in-app.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setNudging(n => ({ ...n, [t.id]: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="team-progress-tab">
      <div className="flex flex-wrap gap-6 text-sm">
        <div><span className="font-display text-2xl">{data.trainee_count}</span> <span className="text-neutral-500">trainees</span></div>
        <div><span className="font-display text-2xl">{data.total_lessons}</span> <span className="text-neutral-500">lessons in curriculum</span></div>
      </div>

      {data.trainees.length === 0 && <div className="text-sm text-neutral-500">No trainees in this workspace yet.</div>}

      <div className="space-y-3">
        {data.trainees.map(t => {
          const stalled = t.days_inactive != null && t.days_inactive > 3;
          return (
            <div key={t.id} className="border hairline bg-white" data-testid={`trainee-row-${t.id}`}>
              <div className="p-4 flex items-center gap-4">
                <button className="text-neutral-400" onClick={() => setExpanded(e => ({ ...e, [t.id]: !e[t.id] }))} data-testid={`trainee-expand-${t.id}`}>
                  {expanded[t.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{t.name}</span>
                    <span className="text-xs text-neutral-500">{t.email}</span>
                    {stalled && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white bg-[#8B2E2E] px-2 py-0.5" data-testid={`trainee-stalled-${t.id}`}>
                        <Clock size={10} /> {t.days_inactive}d inactive
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-3">
                    <div className="progress-hairline flex-1 max-w-md"><div className="fill" style={{ transform: `scaleX(${(t.percent || 0) / 100})` }} /></div>
                    <span className="text-sm font-mono text-neutral-500 w-12 text-right">{t.percent}%</span>
                  </div>
                  <div className="mt-1 text-xs text-neutral-400 font-mono">{t.completed}/{t.total_lessons} lessons · last active {(t.last_activity || "").slice(0, 10) || "—"}</div>
                </div>
                <button
                  onClick={() => nudge(t)}
                  disabled={nudging[t.id]}
                  className={`text-xs flex items-center gap-1 shrink-0 ${stalled ? "btn-primary" : "btn-ghost"}`}
                  data-testid={`nudge-btn-${t.id}`}
                >
                  <Bell size={12} /> {nudging[t.id] ? "Sending…" : "Nudge"}
                </button>
              </div>
              {expanded[t.id] && (
                <div className="border-t hairline p-4 bg-[color:var(--brand-surface2)]/30 grid sm:grid-cols-2 gap-2">
                  {t.modules.map(m => (
                    <div key={m.module_id} className="text-sm flex items-center justify-between border hairline bg-white px-3 py-2">
                      <div className="min-w-0">
                        <div className="label-eyebrow" style={{ fontSize: "0.5rem" }}>{m.stage}</div>
                        <div className="truncate">{m.title}</div>
                      </div>
                      <div className="font-mono text-xs text-neutral-500 shrink-0">{m.done}/{m.total}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
